import React from 'react';
import { Button } from '../components/ui/button';
import { heroTags, stats, IMG, SOCIAL } from '../mock';
import { Terminal, Zap, Activity, Radio } from 'lucide-react';
import { useOffsetClock } from '../hooks/useAppHooks';

// Stable ids for decorative bars (14 waveform bars)
const WAVE_BAR_IDS = Array.from({ length: 14 }, (_, i) => `wave-${i}`);

function TerminalStrip({ clock }) {
  return (
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 mb-6">
      <div className="flex items-center justify-between text-[10px] font-mono tracking-[0.2em] text-cyan-300/60 border-b border-cyan-500/15 pb-2">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> SYS.ONLINE
          </span>
          <span className="hidden md:inline">// LOCATION: MUMBAI_IN</span>
          <span className="hidden md:inline">// GENRE: OPEN_FORMAT</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline">IST {clock}</span>
          <span>v2.6.1</span>
        </div>
      </div>
    </div>
  );
}

function HeroTitle() {
  return (
    <div className="leading-[0.85] relative">
      <h1 className="font-display text-[110px] md:text-[160px] lg:text-[200px] text-white/10 tracking-tight">DJ</h1>
      <div className="relative -mt-4 md:-mt-8">
        <h1 className="font-display text-[110px] md:text-[160px] lg:text-[200px] tracking-tight text-gradient-purple relative z-10">
          YKAS
        </h1>
        <h1 aria-hidden className="font-display text-[110px] md:text-[160px] lg:text-[200px] tracking-tight text-cyan-400/25 absolute inset-0 glitch-layer">
          YKAS
        </h1>
      </div>
    </div>
  );
}

function GenreChips() {
  return (
    <div className="flex flex-wrap gap-2">
      {heroTags.map((tag, i) => (
        <span
          key={tag}
          className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-sm border ${
            i % 2 === 0
              ? 'border-violet-400/50 text-violet-200 bg-violet-500/5'
              : 'border-cyan-400/40 text-cyan-200 bg-cyan-500/5'
          }`}
        >
          [{tag}]
        </span>
      ))}
    </div>
  );
}

function CtaButtons() {
  return (
    <div className="flex flex-wrap gap-3 pt-2">
      <Button asChild className="bg-cyan-400 text-[#05030c] hover:bg-cyan-300 rounded-sm font-mono text-[11px] tracking-[0.25em] uppercase h-12 px-8">
        <a href="#cta">&gt; Book DJ YKAS</a>
      </Button>
      <Button
        asChild
        variant="outline"
        className="bg-transparent border border-violet-400/40 text-violet-100 hover:bg-violet-500/10 hover:text-violet-100 rounded-sm font-mono text-[11px] tracking-[0.25em] uppercase h-12 px-8"
      >
        <a href={SOCIAL.instagram} target="_blank" rel="noreferrer">./Instagram</a>
      </Button>
    </div>
  );
}

function StatsRow() {
  return (
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 mt-20">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-violet-500/15 pt-8">
        {stats.map((s) => (
          <div key={s.label} className="relative p-4 border border-violet-500/10 bg-violet-950/10 rounded-sm">
            <div className="font-mono text-[9px] tracking-[0.25em] text-cyan-300/60 mb-1">{s.hex}</div>
            <div className="font-display text-4xl md:text-5xl text-gradient-purple leading-none">{s.value}</div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-violet-300/50 mt-2">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CornerBrackets() {
  const b = 'absolute w-6 h-6 border-cyan-400';
  return (
    <>
      <span className={`${b} -top-1 -left-1 border-t-2 border-l-2`} />
      <span className={`${b} -top-1 -right-1 border-t-2 border-r-2`} />
      <span className={`${b} -bottom-1 -left-1 border-b-2 border-l-2`} />
      <span className={`${b} -bottom-1 -right-1 border-b-2 border-r-2`} />
    </>
  );
}

function DjPhotoHud() {
  return (
    <>
      <div className="absolute top-0 left-0 right-0 p-3 flex justify-between text-[9px] font-mono tracking-[0.2em] text-cyan-300 z-10">
        <span>SUBJECT_ID: 0xYKAS</span>
        <span className="flex items-center gap-1.5">
          <Radio size={9} className="animate-pulse" /> REC ●
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3 flex justify-between items-end text-[9px] font-mono tracking-[0.2em] text-cyan-200 z-10">
        <div className="space-y-1">
          <div className="text-cyan-300/70">// SCAN.COMPLETE</div>
          <div className="text-white/80 text-[11px]">DJ YKAS · MUMBAI</div>
        </div>
        <div className="flex items-end gap-[2px] h-6">
          {WAVE_BAR_IDS.map((id) => (
            <span key={id} className="wave-bar w-[3px] bg-cyan-300 rounded-sm" style={{ height: '40%' }} />
          ))}
        </div>
      </div>
    </>
  );
}

function DjPhotoPanel() {
  return (
    <div className="relative w-full max-w-[520px] mx-auto">
      <CornerBrackets />
      <div className="relative rounded-sm overflow-hidden border border-cyan-400/40 neon-cyan bg-gradient-to-b from-cyan-950/30 to-violet-950/40">
        <div className="relative aspect-[3/4] scanlines">
          <img src={IMG.djHero} alt="DJ YKAS" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05030c] via-transparent to-cyan-950/20" />
          <DjPhotoHud />
        </div>
      </div>

      <div className="absolute -left-4 top-1/4 hidden md:block">
        <div className="px-3 py-2 bg-[#05030c] border border-violet-400/50 rounded-sm shadow-lg">
          <div className="font-mono text-[9px] tracking-[0.2em] text-violet-300/60">// BPM</div>
          <div className="font-tech text-2xl text-violet-200 font-bold leading-none">128</div>
        </div>
      </div>
      <div className="absolute -right-4 bottom-1/4 hidden md:block">
        <div className="px-3 py-2 bg-[#05030c] border border-cyan-400/50 rounded-sm shadow-lg">
          <div className="font-mono text-[9px] tracking-[0.2em] text-cyan-300/60">// KEY</div>
          <div className="font-tech text-2xl text-cyan-200 font-bold leading-none">A♭</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[9px] tracking-[0.2em]">
        <div className="px-2 py-1.5 border border-violet-500/20 rounded-sm bg-violet-950/20 text-violet-300/70 flex items-center gap-1.5">
          <Zap size={10} className="text-violet-400" /> PWR.100%
        </div>
        <div className="px-2 py-1.5 border border-cyan-500/20 rounded-sm bg-cyan-950/20 text-cyan-300/70 flex items-center gap-1.5">
          <Activity size={10} className="text-cyan-400" /> SIG.STRONG
        </div>
        <div className="px-2 py-1.5 border border-fuchsia-500/20 rounded-sm bg-fuchsia-950/20 text-fuchsia-300/70 flex items-center gap-1.5">
          <Radio size={10} className="text-fuchsia-400" /> LIVE.ON
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const clock = useOffsetClock(330); // IST = UTC+5:30

  return (
    <section id="top" className="relative min-h-screen pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="pointer-events-none absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full bg-violet-600/15 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -right-20 w-[600px] h-[600px] rounded-full bg-cyan-500/12 blur-[120px]" />

      <TerminalStrip clock={clock} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7 space-y-7">
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-violet-300/80 uppercase">
            <Terminal size={13} className="text-cyan-300" />
            <span className="text-cyan-300">$</span>
            <span>init dj_ykas --mode=live</span>
            <span className="animate-blink text-cyan-300">_</span>
          </div>

          <HeroTitle />

          <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-cyan-300 uppercase">
            <Activity size={14} />
            <span>Feel The Rhythm</span>
            <span className="text-cyan-300/50">// BPM_128</span>
          </div>

          <GenreChips />

          <p className="text-base md:text-lg text-white/60 max-w-2xl leading-relaxed border-l-2 border-cyan-400/40 pl-5 font-body">
            <span className="font-mono text-[10px] tracking-[0.25em] text-cyan-300/60 block mb-1.5">// bio.md</span>
            Corporate Leader by Day. Beatmaker by Night. DJ YKAS crafts genre-fluid live sets that fuse Bollywood soul, Afro grooves, and global beats — from intimate birthdays to high-energy club nights across Mumbai.
          </p>

          <CtaButtons />
        </div>

        <div className="lg:col-span-5 relative">
          <DjPhotoPanel />
        </div>
      </div>

      <StatsRow />
    </section>
  );
}
